import { CalendarEvent } from '../types/calendar';

// ============================================================================
// Types for Shared Components
// ============================================================================

export interface CalendarBillDisplay {
  number: string;
  id?: number;
  title?: string;
  concerning?: string;
  status?: string;
  is_tracked?: boolean;
  sponsors?: Array<{
    name: string;
    legislator_id?: number;
    sponsor_type?: string;
    party?: string;
  }>;
}

export interface CalendarEventDisplay {
  calendar_id: number;
  reading_title: string;
  calendar_description?: string;
  reading_time?: string;
  reading_date: string;
  room_number?: string;
  no_time?: boolean;
  upon_adjournment?: boolean;
  upon_adjournment_of_committee_hearing?: boolean;
  chamber?: string;
  joint_meeting?: boolean;
  committee_flag?: boolean;
  agenda_url?: string;
  audio_url?: string;
  committee_url?: string;
  session_year?: string;
  meeting_type?: string;
  /** Derived location display (e.g., "Senate Chamber", "House Education") */
  derived_location?: string;
  /** Location type: 'floor' for chamber events, 'committee' for committee meetings */
  location_type?: 'floor' | 'committee';
  bills?: CalendarBillDisplay[];
  bill_numbers?: string[];
}

export interface ChamberBadgeInfo {
  label: string;
  colorClass: string;
}

// ============================================================================
// Time and Date Formatting
// ============================================================================
// Single source for calendar time display. Backend and scraper always normalize
// null/00:00 to Hour of Convening (see backend dateUtils.normalizeReadingTimeForDisplay).
// Frontend never shows "TBD" — show formatted time or empty.

/**
 * Format a raw time string (HH:MM or HH:MM:SS) to 12-hour display.
 * Use for labels/titles when you only have a time string (backend already normalized).
 */
export const formatTimeStringForDisplay = (time?: string): string => {
  if (!time) return '';
  const parts = time.split(':');
  const hour = parseInt(parts[0], 10);
  const minutes = parts[1] ?? '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

/** Minimal shape needed for time display (e.g. from BillRow in day view). */
export type CalendarEventTimeInput = Pick<
  CalendarEventDisplay,
  'reading_time' | 'upon_adjournment' | 'upon_adjournment_of_committee_hearing'
> & { no_time?: boolean };

/**
 * Format time display for calendar events.
 * Single source for calendar time display (day view, cards, table, exports).
 * Never shows "TBD" — backend sends normalized times; fallback is empty string.
 */
export const formatTimeDisplay = (event: CalendarEventDisplay | CalendarEventTimeInput): string => {
  if (event.upon_adjournment) return 'Upon Adjournment';
  if (event.upon_adjournment_of_committee_hearing) return 'Upon Adjournment of Committee';
  if (event.reading_time) return formatTimeStringForDisplay(event.reading_time);
  return '';
};

/**
 * Get chamber badge info with Tailwind color classes
 */
export const getChamberBadge = (event: CalendarEventDisplay): ChamberBadgeInfo => {
  if (event.joint_meeting) {
    return { 
      label: 'Joint', 
      colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' 
    };
  }
  if (event.chamber?.toLowerCase() === 'senate') {
    return { 
      label: 'Senate', 
      colorClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' 
    };
  }
  if (event.chamber?.toLowerCase() === 'house') {
    return { 
      label: 'House', 
      colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
    };
  }
  return { 
    label: 'Unknown', 
    colorClass: 'bg-background-secondary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary' 
  };
};

// ============================================================================
// Event Detection
// ============================================================================

// Enhanced committee detection - matches backend logic
export const isCommitteeEvent = (event: CalendarEvent): boolean => {
  const title = event.reading_title?.toLowerCase() || '';
  
  return event.committee_flag || 
         title.includes('committee') || 
         title.includes('budget committee') ||
         title.includes('finance committee') ||
         title.includes('appropriations') ||
         title.includes('judiciary committee') ||
         title.includes('health committee') ||
         title.includes('education committee') ||
         title.includes('transportation committee') ||
         title.includes('agriculture committee') ||
         title.includes('energy committee') ||
         title.includes('business committee') ||
         title.includes('local government committee') ||
         title.includes('state affairs committee');
};

// Check if event is a joint committee
export const isJointCommitteeEvent = (event: CalendarEvent): boolean => {
  const title = event.reading_title?.toLowerCase() || '';
  
  return title.includes('joint budget committee') || 
         title.includes('joint committee') ||
         (event.joint_meeting && title.includes('committee'));
};

// Get the color scheme for different event types
export const getEventColor = (event: CalendarEvent): string => {
  if (event.joint_meeting) {
    return 'bg-yellow-100 border-yellow-400 text-yellow-800';
  }
  
  if (event.chamber) {
    const chamber = event.chamber.toLowerCase();
    if (chamber === 'senate') {
      return 'bg-blue-100 border-blue-400 text-blue-800';
    }
    if (chamber === 'house') {
      return 'bg-green-100 border-green-400 text-green-800';
    }
  }
  
  if (isCommitteeEvent(event)) {
    return 'bg-purple-100 border-purple-400 text-purple-800';
  }
  
  // Default color
  return 'bg-background-tertiary border-neutral-400 text-neutral-800';
};

// Get the dot color for calendar indicators
export const getEventDotColor = (event: CalendarEvent): string => {
  if (event.joint_meeting) {
    return 'bg-yellow-400';
  }
  
  if (event.chamber) {
    const chamber = event.chamber.toLowerCase();
    if (chamber === 'senate') {
      return 'bg-blue-400';
    }
    if (chamber === 'house') {
      return 'bg-green-400';
    }
  }
  
  if (isCommitteeEvent(event)) {
    return 'bg-purple-400';
  }
  
  return 'bg-neutral-400';
};

// Format time for display
export const formatEventTime = (time?: string, noTime?: boolean, uponAdjournment?: boolean): string => {
  if (noTime || uponAdjournment) {
    return uponAdjournment ? 'Upon Adjournment' : 'No Time Specified';
  }
  
  if (!time) {
    return 'All Day';
  }
  
  // Convert 24-hour time to 12-hour format
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  
  return `${displayHour}:${minutes} ${ampm}`;
};

// Format date for display
export const formatEventDate = (date: string): string => {
  if (!date) {
    return 'Invalid Date';
  }
  
  try {
    // Extract date part if it's a full timestamp
    const dateStr = date.includes('T') ? date.split('T')[0] : date;
    
    // Parse the date string manually to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Validate the parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day) || 
        year < 1900 || year > 2100 || 
        month < 1 || month > 12 || 
        day < 1 || day > 31) {
      return 'Invalid Date';
    }
    
    const eventDate = new Date(year, month - 1, day); // month is 0-based
    
    // Check if the date is valid
    if (isNaN(eventDate.getTime())) {
      return 'Invalid Date';
    }
    
    return eventDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.warn('Error formatting date:', date, error);
    return 'Invalid Date';
  }
};

// Get chamber icon/label
export const getChamberInfo = (event: CalendarEvent): { label: string; icon: string } => {
  if (event.joint_meeting) {
    return { label: 'Joint', icon: '🤝' };
  }
  
  if (event.chamber) {
    const chamber = event.chamber.toLowerCase();
    if (chamber === 'senate') {
      return { label: 'Senate', icon: '🏛️' };
    }
    if (chamber === 'house') {
      return { label: 'House', icon: '🏛️' };
    }
  }
  
  return { label: 'Legislative', icon: '📋' };
};

// Check if date is in demo data range
export const isInDemoRange = (date: Date): boolean => {
  const demoStart = new Date('2025-01-08');
  const demoEnd = new Date('2025-05-07');
  return date >= demoStart && date <= demoEnd;
};

// Get calendar grid for a month
export const getCalendarGrid = (year: number, month: number): Date[][] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDate = new Date(firstDay);
  
  // Start from Sunday of the week containing the first day
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  const dayOfWeek = startDate.getDay();
  // Calculate days to subtract to get to Sunday (0 = Sunday)
  startDate.setDate(startDate.getDate() - dayOfWeek);
  
  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];
  
  for (let i = 0; i < 42; i++) { // 6 weeks max
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    
    currentWeek.push(currentDate);
    
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
      
      // Stop if we've passed the last day and completed a week
      if (currentDate > lastDay) {
        break;
      }
    }
  }
  
  return weeks;
};

// Group events by date
export const groupEventsByDate = (events: CalendarEvent[]): Record<string, CalendarEvent[]> => {
  return events.reduce((groups, event) => {
    // Extract just the date part (YYYY-MM-DD) from the ISO timestamp
    const date = event.reading_date.split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {} as Record<string, CalendarEvent[]>);
};

// Sort events by time
export const sortEventsByTime = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    // Sort by calendar_id (natural order from Colorado.gov API)
    return a.calendar_id - b.calendar_id;
  });
};

// Get month names
export const getMonthNames = (): string[] => {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
};

// Get day names
export const getDayNames = (): string[] => {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
};

// Format date to YYYY-MM-DD without timezone conversion
export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns true if the array contains at least one event whose date falls on or
 * after today (local date).  Accepts the loose event shapes used across bill
 * cards (reading_date / date / event_date, as Date objects or ISO strings).
 *
 * Use this to guard any "Calendar Date" UI box so it only renders when
 * CalendarEventDisplay will actually have an upcoming event to show.
 */
export const hasUpcomingCalendarEvent = (events: any[] | undefined | null): boolean => {
  if (!events || events.length === 0) return false;
  const today = new Date(new Date().toDateString());
  return events.some(e => {
    const raw = e?.reading_date ?? e?.date ?? e?.event_date;
    if (!raw) return false;
    const dateOnly = String(raw).split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return false;
    return new Date(dateOnly + 'T00:00:00') >= today;
  });
};